import { post } from "aws-amplify/api";

// Simple client-side cache
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const recommendationCache = {};

// Deduplication for interactions
const interactionDedupeCache = {};
const DEDUP_TTL = 60 * 1000; // 1 minute

export const RecommendationService = {
  async getMainFeedRecommendations(userId) {
    try {
      // Check cache first
      const cacheKey = `main_${userId}`;
      if (recommendationCache[cacheKey] && 
          (Date.now() - recommendationCache[cacheKey].timestamp) < CACHE_DURATION) {
        return recommendationCache[cacheKey].data;
      }
      
      const response = await post({
        apiName: 'recommendationApi',
        path: '/recommendations',
        options: {
          body: {
            userId,
            requestType: 'mainFeed'
          }
        }
      });
      
      // Cache the results
      const recommendations = response.body.recommendations || [];
      recommendationCache[cacheKey] = {
        data: recommendations,
        timestamp: Date.now()
      };
      
      return recommendations;
    } catch (error) {
      console.error('Error fetching main feed recommendations:', error);
      // Return cached data if available, even if expired
      if (recommendationCache[`main_${userId}`]) {
        return recommendationCache[`main_${userId}`].data;
      }
      return [];
    }
  },
  
  async getDiscoverRecommendations(userId) {
    try {
      // Check cache first
      const cacheKey = `discover_${userId}`;
      if (recommendationCache[cacheKey] && 
          (Date.now() - recommendationCache[cacheKey].timestamp) < CACHE_DURATION) {
        return recommendationCache[cacheKey].data;
      }
      
      const response = await post({
        apiName: 'recommendationApi',
        path: '/recommendations',
        options: {
          body: {
            userId,
            requestType: 'discover'
          }
        }
      });
      
      // Cache the results
      const recommendations = response.body.recommendations || [];
      recommendationCache[cacheKey] = {
        data: recommendations,
        timestamp: Date.now()
      };
      
      return recommendations;
    } catch (error) {
      console.error('Error fetching discover recommendations:', error);
      // Return cached data if available, even if expired
      if (recommendationCache[`discover_${userId}`]) {
        return recommendationCache[`discover_${userId}`].data;
      }
      return [];
    }
  },
  
  async getRelatedArticles(articleId) {
    try {
      // Check cache first
      const cacheKey = `related_${articleId}`;
      if (recommendationCache[cacheKey] && 
          (Date.now() - recommendationCache[cacheKey].timestamp) < CACHE_DURATION) {
        return recommendationCache[cacheKey].data;
      }
      
      const response = await post({
        apiName: 'recommendationApi',
        path: '/recommendations',
        options: {
          body: {
            articleId,
            requestType: 'related'
          }
        }
      });
      
      // Cache the results
      const recommendations = response.body.recommendations || [];
      recommendationCache[cacheKey] = {
        data: recommendations,
        timestamp: Date.now()
      };
      
      return recommendations;
    } catch (error) {
      console.error('Error fetching related articles:', error);
      // Return cached data if available, even if expired
      if (recommendationCache[`related_${articleId}`]) {
        return recommendationCache[`related_${articleId}`].data;
      }
      return [];
    }
  },
  
  async trackInteraction(userId, articleId, interactionType, duration = 0) {
    try {
      // Skip if missing required parameters
      if (!userId || !articleId || !interactionType) {
        console.error('Missing required parameters for trackInteraction');
        return;
      }
      
      // Deduplication check
      const dedupeKey = `${userId}_${articleId}_${interactionType}`;
      if (interactionDedupeCache[dedupeKey]) {
        return; // Skip duplicate interactions
      }
      
      // Mark as seen for deduplication
      interactionDedupeCache[dedupeKey] = true;
      setTimeout(() => {
        delete interactionDedupeCache[dedupeKey];
      }, DEDUP_TTL);
      
      await post({
        apiName: 'recommendationApi',
        path: '/interactions',
        options: {
          body: {
            userId,
            articleId,
            interactionType,
            duration
          }
        }
      });
    } catch (error) {
      console.error('Error tracking interaction:', error);
      // Don't throw - this is a non-critical operation
    }
  }
};